import * as React from "react";
import { ContextStore } from "../../../../../../../../react-core/Flux/Variables";
import { ICheckboxEditorProps } from "../../../../../../../../react-core/Html/CheckboxEditor";
import { RadiosEditor } from "../../../../../../../../react-core/Html/RadiosEditor";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { ContextType } from "../../../../../../../../Wrap/Constants/Enums/ContextType";
import { ReportView } from "../../../../../../../../Wrap/Constants/Enums/Report/ReportView";
import { UserClaim } from "../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { ClientAccount } from "../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { ReportBuilder } from "../../../../../../../../Wrap/Models/Models/Reports/ReportBuilder";
import { CheckboxGroup } from "../../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { DateRange } from "../../../../../../../Shared/Components/DateRange";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import { checkPermission } from "../../../../../../../Shared/Permission/PermissionHelper";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { subAccountTypeMap } from "../../Performance/Resource/Contents";
import { ReportFormatArray, ReportFormatMap, ReportViewArray, ReportViewMap } from "../Resource/Content";
import { ClientReportPortfolio } from "./ClientReportPortfolio";
import { ImpactRedirectAction } from "../../../../../../../Shared/Impact/Action/ImpactRedirectAction";
import { ImpactRedirectType } from "../../../../../../../../Wrap/Models/Domains/Impact/ImpactRedirectType";

interface ISetupProps extends IProcessComponentProps {
    clientAccount: ClientAccount;
    data: ReportBuilder;
}

interface ISetupState {
    dateSelected: string;
    errorMsg: boolean;
}

export class Setup extends ProcessComponent<ISetupProps, ISetupState> {
    constructor(props: ISetupProps) {
        super(props);
        this.state = {
            dateSelected: null,
            errorMsg: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        const clientInfo = this.props.clientAccount;
        const openingDate = clientInfo ? new Date(clientInfo.InceptionDate as any) : null;
        const initDateRange = DateRange.initializeLastYearDateRange(openingDate);
        this.props.onChange({
            From: JsonDateParser.toJsonDateNoOffset(initDateRange.from),
            To: JsonDateParser.toJsonDateNoOffset(initDateRange.to),
        });
        this.setState({ dateSelected: initDateRange.dateSelected });
    }

    isValid() {
        const flag = super.isValid();
        if (this.props.data?.ReportView == ReportView.ProductView && this.props.data?.SubAccounts.length == 0) {
            this.setState({ errorMsg: true });
            return false;
        }
        return flag;
    }

    private getSubAccountsOptions(): ICheckboxEditorProps[] {
        const clientInfo = this.props.clientAccount;
        if (!clientInfo?.SubAccounts) {return [];}
        return clientInfo.SubAccounts.map((ele) => {
            return {
                label: `${subAccountTypeMap[ele.SubAccountType]} ${ele.ClAccountId}`,
                value: this.props.data.SubAccounts.some((item) => item == ele.HierarchyId),
                onChange: (v) => {
                    let subAccounts = this.props.data.SubAccounts.concat();
                    if (v) {
                        subAccounts.push(ele.HierarchyId);
                    } else {
                        subAccounts = subAccounts.filter((item) => item != ele.HierarchyId);
                    }
                    this.props.onChange({ SubAccounts: subAccounts });
                },
            };
        });
    }

    render() {
        const { minDate, isDisable } = DateRange.getLeastDate(
            ContextStore.getUrlParamsHierarchyId(ContextType.Account)
        );
        const { data, onChange } = this.props;
        const toDate = JsonDateParser.toDateNoOffset(data.To);
        return (
            <ClientReportPortfolio>
                <div className="row">
                    <div className="col-12 col-sm-7">
                        <h2>Setup</h2>
                        <DateRange
                            label="Select report date(s)"
                            disabled={isDisable}
                            minDate={minDate}
                            fromDate={JsonDateParser.toDateNoOffset(data?.From)}
                            toDate={toDate}
                            cssGridDate="col-12 col-sm-auto"
                            cssGridTo="col-12 col-sm-auto"
                            cssGridFrom="col-12 col-sm-auto"
                            cssGridLabel="col-12 col-sm-auto col-middle"
                            selectValue={this.state.dateSelected}
                            onChange={(fromDate: Date, toDate: Date, fromInception: boolean, dateSelected: string) => {
                                this.props.onChange({
                                    From: JsonDateParser.toJsonDateNoOffset(fromDate),
                                    To: JsonDateParser.toJsonDateNoOffset(toDate),
                                    dateSelected: dateSelected,
                                    fromInception: fromInception,
                                });
                            }}
                        />

                        <h3 className="mb-6">Report view</h3>
                        <RadiosEditor
                            options={ReportViewArray}
                            getText={(o) => ReportViewMap[o]}
                            selected={data?.ReportView}
                            onChange={(v) =>
                                onChange({
                                    ReportView: v,
                                    SubAccounts: v == ReportView.TotalPortfolio ? [] : data?.SubAccounts,
                                })
                            }
                            required="Please select"
                            cssWrapper="rg-inline"
                        />
                        {data?.ReportView == ReportView.ProductView && (
                            <>
                                <fieldset className="form-group checkbox-layout-vertical mb-0">
                                    <CheckboxGroup type={"block"} options={this.getSubAccountsOptions()} />
                                </fieldset>

                                {this.state.errorMsg && (
                                    <Alert
                                        alertType={AlertType.Error}
                                        message={"Please select at least one sub account!"}
                                    />
                                )}
                            </>
                        )}
                        <h3 className="mb-6">Format</h3>
                        <RadiosEditor
                            options={ReportFormatArray}
                            getText={(o) => ReportFormatMap[o]}
                            selected={data?.ReportFormat}
                            onChange={(v) => onChange({ ReportFormat: v })}
                            required="Please select"
                            cssWrapper="rg-inline"
                        />
                    </div>
                    <div className="col-12 col-sm-5">
                        <div className="other-reports-panel">
                            <div className="or-border"></div>
                            <div className="or-body">
                                <h4>Looking for other reports?</h4>
                                <p className="mb-6">
                                    To request other reports such as Capital Gain Report, Investment Transactions,
                                    Statements, go to{" "}
                                    {checkPermission(UserClaim.AdviserPortalDluReportsRequestCanView) ? (
                                        <a
                                            className="hyperlink"
                                            href={`/clients/reports/reports-request.aspx?claccountid=${
                                                this.props.clientAccount?.HeadClAccountId
                                            }&customerhierarchyid=${ContextStore.getUrlParamsHierarchyId(
                                                ContextType.Customer
                                            )}`}
                                        >
                                            other client reports.
                                        </a>
                                    ) : (
                                        "other client reports."
                                    )}
                                </p>
                            </div>
                        </div>
                        {!!this.props.clientAccount?.HeadClAccountId && checkPermission(UserClaim.AdviserPortalEsgAnalyserCanView) && 
                        <div className="other-reports-panel">
                            <div className="or-border"></div>
                            <div className="or-body">
                                <h4>Looking for an ESG report?</h4>
                                <p className="mb-6">
                                    To request an ESG report, go to&nbsp;
                                    <button 
                                        className="hyperlink" 
                                        onClick={() => ImpactRedirectAction.redirectToImpact(this.props.clientAccount?.HeadClAccountId, ImpactRedirectType.Reporting)}>
                                        ESG Hub
                                    </button>
                                    .
                                </p>
                            </div>
                        </div>}
                    </div>
                </div>
            </ClientReportPortfolio>
        );
    }
}
